import { useCallback, useMemo, useState } from 'react';
import { Nav, TabContainer, TabContent, TabPane } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { AppErrorBoundaryComponent } from '../../../../components/error-boundary/app-error-boundary.component';
import { FILTER_NAMES } from '../../../../components/filters/hooks/filter-names.constants';
import { FilterSelectedValue } from '../../../../hooks/filters/interfaces/filter-selected-value.interface';
import { useScenarioPlannerReset } from '../../../../hooks/scenario-planner/use-scenario-planner-reset.hook';

import { useMediaClassName } from '../../../../hooks/use-window-resize.hook';
import './scenario-planner-edit.modal.scss';
import '../../../../components/filters/components/filter-tab-button/filter-tab-button.component.scss';
import { postScenarioPlannerInput } from '../../../../services/scenario-planner.service';
import { MassEditBlock } from '../mass-edit-sidebar/mass-edit-sidebar.component';
import {
  MassEditBlockCross,
  ScenarioPlannerEditCrossElastComponent,
} from './scenario-planner-edit-cross-elast.component';
import { ScenarioPlannerEditMainElastComponent } from './scenario-planner-edit-main-elast.component';
import { ScenarioPlannerEditResetButtonComponent } from './scenario-planner-edit-reset-button.component';
import ScenarioPlannerEditStartingMetricsComponent from './scenario-planner-edit-starting-metrics';

export interface ScenarioPlannerEditModalComponentProps {
  show: boolean;
  onClose: (show: boolean) => void;
  rowId: string;
  scenarioPlannerId: string;
  onSaveComplete?: () => void;
  rowData?: {
    category_name?: string;
    name?: string;
  };
  mainElastBlocks: MassEditBlock[];
  crossElastBlocks: MassEditBlock[];
  startingMetricsBlocks: MassEditBlock[];
  isViewOnly: boolean;
}

export const ScenarioPlannerEditModalComponent = ({
  onClose,
  show,
  rowId,
  scenarioPlannerId,
  onSaveComplete,
  rowData,
  mainElastBlocks,
  crossElastBlocks,
  startingMetricsBlocks,
  isViewOnly,
}: ScenarioPlannerEditModalComponentProps) => {
  const handleClose = useCallback(() => onClose && onClose(false), [onClose]);
  const mediaClassName = useMediaClassName();
  const [activeTab, setActiveTab] = useState<string>('elasticities');
  const [currentScreen, setCurrentScreen] = useState<'first' | 'second'>(
    'first',
  );

  // Tab options for navigation
  const tabOptions = [
    { value: 'elasticities', label: 'Elasticities' },
    { value: 'baseMetrics', label: 'Base Metrics' },
  ];

  const onResetCallback = useCallback(() => {
    onSaveComplete?.();
    handleClose();
  }, [onSaveComplete, handleClose]);

  // Create filter selection with the main product only
  const applyToFilterSelection: FilterSelectedValue[] = useMemo(
    () => [
      {
        id: FILTER_NAMES.PROD,
        values: [
          {
            label: '', // Not required
            value: rowId,
          },
        ],
      },
    ],
    [rowId],
  );

  const { handleReset: handleResetAll, isResetting: isResettingAll } =
    useScenarioPlannerReset(
      scenarioPlannerId,
      applyToFilterSelection,
      mainElastBlocks.concat(startingMetricsBlocks),
      crossElastBlocks,
      onResetCallback,
      'main',
    );
  const {
    handleReset: handleResetElasticities,
    isResetting: isResettingElasticities,
  } = useScenarioPlannerReset(
    scenarioPlannerId,
    applyToFilterSelection,
    mainElastBlocks,
    crossElastBlocks,
    onResetCallback,
    'main',
  );
  const {
    handleReset: handleResetStartingMetrics,
    isResetting: isResettingStartingMetrics,
  } = useScenarioPlannerReset(
    scenarioPlannerId,
    applyToFilterSelection,
    startingMetricsBlocks,
    [],
    onResetCallback,
    'main',
  );
  // Get current row data from props
  const currentRowData = useMemo(() => {
    if (rowData && rowData.category_name && rowData.name) {
      return {
        category_name: rowData.category_name,
        name: rowData.name,
      };
    }
    return {
      category_name: 'Unknown Category',
      name: 'Unknown Product',
    };
  }, [rowData]);

  const [inputFieldsMain, setInputFieldsMain] = useState<MassEditBlock[]>([]);
  const [invalidateQueryMain, setInvalidateQueryMain] = useState<{
    invalidateQuery: () => Promise<void>;
  }>();

  const handleBack = useCallback(() => {
    setCurrentScreen('first');
  }, []);

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleSaveStarting = useCallback(
    (inputFields: MassEditBlock[], invalidateQuery: () => Promise<void>) => {
      setIsSaving(true);
      if (inputFields.length > 0) {
        postScenarioPlannerInput(
          scenarioPlannerId,
          inputFields,
          applyToFilterSelection,
          true,
          'main',
        ).finally(() => {
          invalidateQuery();
          if (onSaveComplete) {
            onSaveComplete();
          }
          if (onClose) {
            onClose(false);
          }
        });
      } else {
        if (onClose) {
          onClose(false);
        }
      }
      setIsSaving(false);
    },
    [scenarioPlannerId, applyToFilterSelection, onSaveComplete, onClose],
  );

  const handleSaveMain = useCallback(
    async (
      inputFields: MassEditBlock[],
      invalidateQueryObj?: { invalidateQuery: () => Promise<void> },
    ) => {
      if (inputFields.length > 0) {
        await postScenarioPlannerInput(
          scenarioPlannerId,
          inputFields,
          applyToFilterSelection,
          true,
          'main',
        );
        if (invalidateQueryObj?.invalidateQuery) {
          invalidateQueryObj?.invalidateQuery();
        }
      }
    },
    [scenarioPlannerId, applyToFilterSelection],
  );
  const handleSaveCross = useCallback(
    async (
      inputFields: MassEditBlockCross[],
      invalidateQuery?: () => Promise<void>,
    ) => {
      if (inputFields.length > 0) {
        await postScenarioPlannerInput(
          scenarioPlannerId,
          inputFields,
          applyToFilterSelection,
          true,
          'cross',
        );
        if (invalidateQuery) {
          invalidateQuery();
        }
      }
    },
    [scenarioPlannerId, applyToFilterSelection],
  );

  const handleMainNext = useCallback(
    (inputFields: MassEditBlock[], invalidateQuery: () => Promise<void>) => {
      // Store main input field and invalidate function to be used at the last step.
      setInputFieldsMain(inputFields);
      setInvalidateQueryMain({ invalidateQuery: invalidateQuery });
      setCurrentScreen('second');
    },
    [],
  );

  const handleSaveMainAndCross = useCallback(
    async (
      inputFields: MassEditBlockCross[],
      invalidateQuery: () => Promise<void>,
    ) => {
      setIsSaving(true);
      await handleSaveCross(inputFields, invalidateQuery);
      await handleSaveMain(inputFieldsMain, invalidateQueryMain);
      onSaveComplete?.();
      onClose(false);
      setIsSaving(false);
    },
    [
      handleSaveCross,
      handleSaveMain,
      inputFieldsMain,
      invalidateQueryMain,
      onClose,
      onSaveComplete,
    ],
  );
  const isDisabled = useMemo(
    () =>
      isViewOnly ||
      isSaving ||
      isResettingAll ||
      isResettingElasticities ||
      isResettingStartingMetrics,
    [
      isResettingAll,
      isResettingElasticities,
      isResettingStartingMetrics,
      isSaving,
      isViewOnly,
    ],
  );
  return (
    <Modal
      className={`${mediaClassName} scenario-planner-edit-modal`}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={show}
      onHide={handleClose}
      size="lg"
    >
      <Modal.Header closeButton className="d-flex justify-content-between">
        <Modal.Title className="scenario-planner-edit-modal-headertitle d-flex justify-content-start align-items-center">
          Edit Pack - {currentRowData.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {/* Tab Section */}
        <div className="p-3 border-bottom">
          <Nav
            className="filter-tab-button"
            variant="underline"
            defaultActiveKey={activeTab}
          >
            {tabOptions.map((option) => (
              <Nav.Item
                key={option.value}
                onClick={() => setActiveTab(option.value)}
              >
                <Nav.Link active={activeTab === option.value}>
                  {option.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>

        {/* Scrollable Content Area */}
        <div className="modal-content-scrollable">
          <TabContainer defaultActiveKey={activeTab} activeKey={activeTab}>
            <TabContent>
              <TabPane eventKey={'elasticities'}>
                <TabContainer
                  defaultActiveKey={currentScreen}
                  activeKey={currentScreen}
                >
                  <TabContent>
                    <TabPane eventKey={'first'}>
                      <AppErrorBoundaryComponent>
                        <ScenarioPlannerEditMainElastComponent
                          onClose={onClose}
                          onComplete={handleMainNext}
                          scenarioPlannerId={scenarioPlannerId}
                          disabled={isDisabled}
                          applyToFilterSelection={applyToFilterSelection}
                          resetButton={
                            <ScenarioPlannerEditResetButtonComponent
                              disabled={isDisabled}
                              onClick={handleResetAll}
                              onResetElastClick={handleResetElasticities}
                              onResetStartingMetricsClick={
                                handleResetStartingMetrics
                              }
                              onResetAllClick={handleResetAll}
                              busy={
                                isResettingAll ||
                                isResettingElasticities ||
                                isResettingStartingMetrics
                              }
                              label="Reset All"
                              busyLabel="Resetting..."
                            />
                          }
                        />
                      </AppErrorBoundaryComponent>
                    </TabPane>
                    <TabPane eventKey={'second'}>
                      <AppErrorBoundaryComponent>
                        <ScenarioPlannerEditCrossElastComponent
                          isSaving={isSaving}
                          onClose={onClose}
                          onComplete={handleSaveMainAndCross}
                          onBack={handleBack}
                          scenarioPlannerId={scenarioPlannerId}
                          disabled={isDisabled}
                          applyToFilterSelection={applyToFilterSelection}
                          resetButton={
                            <ScenarioPlannerEditResetButtonComponent
                              disabled={isDisabled}
                              onClick={handleResetAll}
                              onResetElastClick={handleResetElasticities}
                              onResetStartingMetricsClick={
                                handleResetStartingMetrics
                              }
                              onResetAllClick={handleResetAll}
                              busy={
                                isResettingAll ||
                                isResettingElasticities ||
                                isResettingStartingMetrics
                              }
                              label="Reset All"
                              busyLabel="Resetting..."
                            />
                          }
                        />
                      </AppErrorBoundaryComponent>
                    </TabPane>
                  </TabContent>
                </TabContainer>
              </TabPane>
              <TabPane eventKey={'baseMetrics'}>
                {/* Base Metrics Tab Content */}
                {scenarioPlannerId && (
                  <AppErrorBoundaryComponent>
                    <ScenarioPlannerEditStartingMetricsComponent
                      isSaving={isSaving}
                      scenarioPlannerId={scenarioPlannerId}
                      onClose={() => handleClose()}
                      onComplete={handleSaveStarting}
                      disabled={isDisabled}
                      applyToFilterSelection={applyToFilterSelection}
                      resetButton={
                        <ScenarioPlannerEditResetButtonComponent
                          disabled={isDisabled}
                          onClick={handleResetAll}
                          onResetElastClick={handleResetElasticities}
                          onResetStartingMetricsClick={
                            handleResetStartingMetrics
                          }
                          onResetAllClick={handleResetAll}
                          busy={
                            isResettingAll ||
                            isResettingElasticities ||
                            isResettingStartingMetrics
                          }
                          label="Reset All"
                          busyLabel="Resetting..."
                        />
                      }
                    />
                  </AppErrorBoundaryComponent>
                )}
              </TabPane>
            </TabContent>
          </TabContainer>
        </div>
      </Modal.Body>
    </Modal>
  );
};
